// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logPageAction } from './../../lib/ApplicationInsights';
import { saveUserFeedback } from './../../businessLogic/sagas/feedback';
import { actionStatusSelector } from './../../store/actionStatus';

import DialogForm from './DialogForm';

export interface PropsType {
    children: React.ReactNode;
    isOpen: boolean;
    onClose: any;
}

export default function Feedback(props: PropsType): any {
    const dispatch = useDispatch();

    const [feedbackType, setFeedbackType] = React.useState<string>('');
    const [feedbackTitle, setFeedbackTitle] = React.useState<string>('');
    const [feedbackDetails, setFeedbackDetails] = React.useState<string>('');
    const [includeScreenshots, setIncludeScreenshots] = React.useState<boolean>(true);
    const [screenshot, setScreenshot] = React.useState<string | null>(null);

    const submitCallStatus = useSelector(state =>
        actionStatusSelector(state)(saveUserFeedback.toString())
    );

    function onCategoryChange(key: string, text: string) {
        logPageAction('Feedback dropdown item selected', {
            itemSelected: text,
        });
        setFeedbackType(key.toString());
    }

    function onTitleChange(event: any, value?: string) {
        setFeedbackTitle(value || '');
    }

    function onDetailsChange(event: any, value?: string) {
        setFeedbackDetails(value || '');
    }

    function onIncludeScreenshotsChange() {
        setIncludeScreenshots(!includeScreenshots);
    }

    function onScreenshotCaptured(value: string) {
        setScreenshot(value);
    }

    function onFeedbackSubmitClick() {
        logPageAction('Feedback submitted', {
            type: feedbackType,
            title: feedbackTitle,
            details: feedbackDetails,
        });

        dispatch(
            saveUserFeedback({
                type: feedbackType,
                title: feedbackTitle,
                details: feedbackDetails,
                screenshot: includeScreenshots ? screenshot : null,
            })
        );
    }

    function onCloseClicked() {
        logPageAction('Feedback dialog closed');

        //Reset previous values that are submitted
        setFeedbackTitle('');
        setFeedbackType('');
        setFeedbackDetails('');

        props.onClose();
    }

    return (
        <>
            {props.children}
            <DialogForm
                data={{
                    feedbackType,
                    feedbackTitle,
                    feedbackDetails,
                    includeScreenshots,
                }}
                isOpen={props.isOpen}
                onCategoryChange={onCategoryChange}
                onCloseClicked={onCloseClicked}
                onDetailsChange={onDetailsChange}
                onFeedbackSubmitClick={onFeedbackSubmitClick}
                onIncludeScreenshotsChange={onIncludeScreenshotsChange}
                onTitleChange={onTitleChange}
                submitCallStatus={submitCallStatus}
                onScreenshotCaptured={onScreenshotCaptured}
            />
        </>
    );
}